import React, { Component, CSSProperties, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import styles from './ErrorBoundary.module.scss';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

// A render crash used to dead-end on a static "Sorry.. there was an error"
// page with no way out but a manual app kill + relaunch + re-login. Give the
// player a one-tap recovery (full reload re-runs auth + reconnect + auto-
// resubscribe) so a transient render error can't strand them mid-tournament.
const ErrorBoundaryPage: React.FC = () => {
  return (
    <div className={styles.wrapper}>
      <h1>Something went wrong</h1>
      <p style={messageStyle}>
        The app hit an unexpected error. Reloading will reconnect you to any tables you have open.
      </p>
      <button type='button' style={reloadButtonStyle} onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
};

class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Uncaught error:', error, errorInfo);
    // Report so render crashes are measurable (no-op until Sentry.init runs —
    // it's env-gated in App.tsx, so this is safe whether or not a DSN is set).
    try {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    } catch {
      /* never let error reporting throw from the boundary */
    }
  }

  public render() {
    if (this.state.hasError) {
      return <ErrorBoundaryPage />;
    }
    return this.props.children;
  }
}

export default GlobalErrorBoundary;

const messageStyle: CSSProperties = {
  maxWidth: 320,
  textAlign: 'center',
  lineHeight: 1.4,
  opacity: 0.85
};

const reloadButtonStyle: CSSProperties = {
  marginTop: '1rem',
  padding: '0.6rem 1.6rem',
  border: 'none',
  borderRadius: '10px',
  background: '#E98C26',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
